import { useTranslation } from 'react-i18next';

import {
  RegisterAbsenceProposalEntry,
  RegisterAbsenceReason
} from 'RestAPIs/roster/register-absence';
import DutyPeriodDialog from 'components/employee-card/employment/hours-table/components/DutyPeriodDialog';

import {
  ContentSection,
  DetailLabel,
  DetailRow,
  DetailsColumn,
  DetailsGrid,
  DetailValue,
  DialogBody,
  Footer,
  LoadingState,
  ReasonOption,
  ReasonSelect,
  SectionPanel,
  SectionTitle,
  Spinner,
  StyledDialog,
  StyledFormControl
} from './LeaveRequestProposalDialog.styles';
import {
  formatDate,
  formatTime,
  toBackendDateTime,
  toEditableDutyLine
} from './LeaveRequestProposalDialog.utils';
import useLeaveRequestProposal from './useLeaveRequestProposal';
import SingleDayProposalView from './SingleDayProposalView';
import RolloutView from './RolloutView';
import Button from '@sdb/ui/atoms/Button';

interface LeaveRequestProposalDialogProps {
  open: boolean;
  leaveRequestId: string;
  leaveType?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const LeaveRequestProposalDialog = ({
  open,
  leaveRequestId,
  leaveType,
  onClose,
  onSuccess
}: LeaveRequestProposalDialogProps) => {
  const { t } = useTranslation('common');

  const {
    reasonCode,
    setReasonCode,
    note,
    proposals,
    setProposals,
    editingDutyIndex,
    setEditingDutyIndex,
    workAgreementSettings,
    updateWorkAgreementSetting,
    isSingleDayLeaveRequest,
    isLoading,
    leaveRequest,
    reasons,
    selectedWorkAgreements,
    requestedWorkAgreement,
    selectedDutyHourType,
    existingRolloutDuties,
    newRolloutDuties,
    saveMutation,
    saveRolloutMutation
  } = useLeaveRequestProposal({ open, leaveRequestId, onClose, onSuccess });

  const removeDutyEntry = (entryToRemove: RegisterAbsenceProposalEntry) => {
    setProposals((current) =>
      current.map((entry) =>
        entry === entryToRemove ? { ...entry, dutyLine: null } : entry
      )
    );
  };

  const removeOpenRequestEntry = (
    entryToRemove: RegisterAbsenceProposalEntry
  ) => {
    setProposals((current) =>
      current.map((entry) =>
        entry === entryToRemove ? { ...entry, openRequest: null } : entry
      )
    );
  };

  const handleDutyPeriodsSave = (
    dutyCodeOption: { label: string } | null,
    periods: Array<{
      dutyHourTypeId?: number | null;
      startTime: string;
      endTime: string;
      dutyHourType?: {
        description?: string;
        dutyHourTypeId?: number | null;
      };
    }>
  ) => {
    if (editingDutyIndex === null) {
      return;
    }

    setProposals((current) =>
      current.map((entry, index) => {
        if (index !== editingDutyIndex || !entry.dutyLine) {
          return entry;
        }

        return {
          ...entry,
          dutyLine: {
            ...entry.dutyLine,
            displayDutyCode:
              dutyCodeOption?.label ??
              entry.dutyLine.displayDutyCode ??
              entry.dutyLine.dutyCode,
            periods: periods.map((period) => ({
              startTime: period.startTime,
              endTime: period.endTime,
              dutyTypeId: period.dutyHourTypeId ?? 0,
              dutyHourType: {
                id:
                  period.dutyHourType?.dutyHourTypeId ??
                  period.dutyHourTypeId ??
                  0,
                description: period.dutyHourType?.description ?? ''
              }
            }))
          }
        };
      })
    );

    setEditingDutyIndex(null);
  };

  const editingDutyRow =
    editingDutyIndex === null
      ? null
      : toEditableDutyLine(proposals[editingDutyIndex], editingDutyIndex);

  const handleSave = () => {
    if (!leaveRequest || !reasonCode) {
      return;
    }

    if (!isSingleDayLeaveRequest) {
      if (!selectedWorkAgreements.length || !selectedDutyHourType) {
        return;
      }

      saveRolloutMutation.mutate({
        startTime: toBackendDateTime(leaveRequest.valid.from),
        endTime: toBackendDateTime(leaveRequest.valid.to),
        dutyTypeId: leaveRequest.dutyTypeId,
        dccSvc: selectedDutyHourType.dccSvc,
        note,
        absenceReasonCode: reasonCode,
        leaveRequestId: leaveRequest.leaveRequestId,
        employeeId: leaveRequest.employeeId,
        workAgreements: selectedWorkAgreements.map((selectedWorkAgreement) => ({
          waId: selectedWorkAgreement.waId,
          benefitId: selectedWorkAgreement.benefitId,
          hoursWeek:
            selectedWorkAgreement.hoursPerWeek ??
            selectedWorkAgreement.weekHoursMin,
          absPercentage: 100,
          dutiesToUpdate: existingRolloutDuties
            .filter(
              (item) =>
                item.action === 'MODIFY' &&
                item.id &&
                item.waId === selectedWorkAgreement.waId
            )
            .map((item) => ({
              dutyLineId: item.id!,
              dutyDatetimeBegin: toBackendDateTime(item.startTime),
              dutyDatetimeEnd: toBackendDateTime(item.endTime)
            })),
          dutiesToDelete: existingRolloutDuties
            .filter(
              (item) =>
                item.action === 'DELETE' &&
                item.id &&
                item.waId === selectedWorkAgreement.waId
            )
            .map((item) => ({
              dutyLineId: item.id!,
              dutyDatetimeBegin: toBackendDateTime(item.startTime),
              dutyDatetimeEnd: toBackendDateTime(item.endTime)
            })),
          dutiesToCreate: newRolloutDuties
            .filter((item) => item.waId === selectedWorkAgreement.waId)
            .map((item) => ({
              dutyDatetimeBegin: toBackendDateTime(item.startTime),
              dutyDatetimeEnd: toBackendDateTime(item.endTime)
            }))
        }))
      });
      return;
    }

    saveMutation.mutate({
      startDateTime: toBackendDateTime(leaveRequest.valid.from),
      endDateTime: toBackendDateTime(leaveRequest.valid.to),
      dutyTypeId: leaveRequest.dutyTypeId,
      absenceReasonCode: reasonCode,
      workAgreementId: leaveRequest.waId,
      leaveRequestId: leaveRequest.leaveRequestId,
      employeeId: leaveRequest.employeeId,
      note,
      proposals
    });
  };

  return (
    <StyledDialog
      open={open}
      handleClose={onClose}
      title={
        leaveRequest
          ? t('plannerMessages.leaveRequestDialog.title', {
              employee: `${leaveRequest.employeeName} - ${leaveRequest.employeeNr}`
            })
          : t('plannerMessages.leaveRequestDialog.titleLoading')
      }
    >
      <DialogBody>
        {isLoading || !leaveRequest ? (
          <LoadingState>
            <Spinner />
            <SectionTitle>
              {t('plannerMessages.leaveRequestDialog.loading')}
            </SectionTitle>
          </LoadingState>
        ) : (
          <>
            <ContentSection>
              <SectionPanel>
                <DetailsGrid>
                  <DetailsColumn>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.start')}
                      </DetailLabel>
                      <DetailValue>
                        {formatDate(leaveRequest.valid.from)}{' '}
                        {formatTime(leaveRequest.valid.from)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.end')}
                      </DetailLabel>
                      <DetailValue>
                        {formatDate(leaveRequest.valid.to)}{' '}
                        {formatTime(leaveRequest.valid.to)}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.leaveType')}
                      </DetailLabel>
                      <DetailValue>{leaveType || '-'}</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.reason')}
                      </DetailLabel>
                      <StyledFormControl>
                        <ReasonSelect
                          value={reasonCode}
                          onChange={(event) =>
                            setReasonCode(String(event.target.value))
                          }
                          displayEmpty
                        >
                          {reasons.map((reason: RegisterAbsenceReason) => (
                            <ReasonOption key={reason.code} value={reason.code}>
                              {reason.name}
                            </ReasonOption>
                          ))}
                        </ReasonSelect>
                      </StyledFormControl>
                    </DetailRow>
                  </DetailsColumn>

                  <DetailsColumn>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.workAgreement')}
                      </DetailLabel>
                      <DetailValue>
                        {requestedWorkAgreement?.nr ?? leaveRequest.waId}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.contractHours')}
                      </DetailLabel>
                      <DetailValue>
                        {requestedWorkAgreement?.hoursPerWeek ??
                          requestedWorkAgreement?.weekHoursMin ??
                          '-'}
                      </DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.percentage')}
                      </DetailLabel>
                      <DetailValue>100</DetailValue>
                    </DetailRow>
                    <DetailRow>
                      <DetailLabel>
                        {t('plannerMessages.leaveRequestDialog.notes')}
                      </DetailLabel>
                      <DetailValue>{note || '-'}</DetailValue>
                    </DetailRow>
                  </DetailsColumn>
                </DetailsGrid>
              </SectionPanel>
            </ContentSection>

            {isSingleDayLeaveRequest ? (
              <SingleDayProposalView
                proposals={proposals}
                onRemoveDuty={removeDutyEntry}
                onRemoveOpenRequest={removeOpenRequestEntry}
                onEditDuty={setEditingDutyIndex}
              />
            ) : (
              <RolloutView
                workAgreement={requestedWorkAgreement}
                workAgreementSettings={workAgreementSettings}
                existingRolloutDuties={existingRolloutDuties}
                newRolloutDuties={newRolloutDuties}
                onUpdateWorkAgreementSetting={updateWorkAgreementSetting}
              />
            )}

            <Footer>
              <Button variant="outlined" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  !reasonCode ||
                  saveMutation.isLoading ||
                  saveRolloutMutation.isLoading ||
                  (!isSingleDayLeaveRequest &&
                    (!selectedWorkAgreements.length || !selectedDutyHourType))
                }
              >
                {t('save')}
              </Button>
            </Footer>

            {leaveRequest && editingDutyRow ? (
              <DutyPeriodDialog
                isOpen={editingDutyIndex !== null}
                onClose={() => setEditingDutyIndex(null)}
                row={editingDutyRow}
                employeeId={leaveRequest.employeeId}
                onSave={handleDutyPeriodsSave}
              />
            ) : null}
          </>
        )}
      </DialogBody>
    </StyledDialog>
  );
};

export default LeaveRequestProposalDialog;
